/**
 * ThemeAI write-back (design spec #916 §11.2, §12; issue #956, Wave 2a).
 *
 * Pure helpers backing the "Write to job variable…" affordance on the
 * Results stage — kept free of hooks/network calls so they're directly
 * unit-testable. The networking itself lives in useThemeAIRuns.
 */

import { ThemeAITask, ThemeAIRun, WriteThemeAIVariableResponse } from '../types/gotypes'
import type { ThemeAIWrittenVariable, ThemeAIWrittenCompanion } from '../components/theme-ai-tasks/ResultsStage'

/** Default variable name the write-back dialog pre-fills (spec §12.3). */
export function defaultWriteVariableName(sourceVariableName: string | undefined): string {
  const name = (sourceVariableName || '').trim()
  return name ? `${name}_themes` : ''
}

/**
 * Write-back is a job-variable-sourced-only affordance (Wave 2a) — the
 * upload-sourced respondent-ID join is Wave 2b. This gates whether the
 * "Write to job variable…" button renders at all (it should NOT appear,
 * rather than appear disabled, for upload-sourced tasks).
 */
export function isThemeAIJobVariableSourced(task: ThemeAITask | undefined | null): boolean {
  return task?.data_source?.type === 'job_variable'
}

/**
 * Whether the task has produced any coded output to publish — used to
 * disable the write-back button until at least one run has actually
 * completed with responses coded (the backend re-validates this; this is
 * just the button-affordance gate).
 */
export function hasCodedThemeAIRun(runs: ThemeAIRun[] | undefined | null): boolean {
  return (runs || []).some((run) => !run.is_test && run.status === 'completed' && run.response_count > 0)
}

// -------------------------------------------------------------------------
// Double-confirm overwrite state machine (spec §12.4)
// -------------------------------------------------------------------------

// 'form'      — editing the variable name, no overwrite conflict yet.
// 'confirm1'  — server 409'd (name exists); asking "overwrite it?".
// 'confirm2'  — confirm #1 accepted; asking "are you absolutely sure?".
export type WriteVariableDialogStep = 'form' | 'confirm1' | 'confirm2'

export type WriteVariableDialogAction =
  | { type: 'CONFLICT' } // the no-overwrite submit came back 409
  | { type: 'CONFIRM' } // user accepted the current confirm step
  | { type: 'RESET' } // dialog closed/reopened — back to the form

/**
 * Pure transition for the double-confirm flow: first submit is always
 * without overwrite; a 409 moves to confirm1; confirming confirm1 moves to
 * confirm2; confirming confirm2 is the caller's cue to resubmit with
 * overwrite:true (the reducer itself doesn't perform the resubmit — the
 * hook/component does, then calls RESET on success/close).
 */
export function writeVariableDialogReducer(
  step: WriteVariableDialogStep,
  action: WriteVariableDialogAction,
): WriteVariableDialogStep {
  switch (action.type) {
    case 'CONFLICT':
      return 'confirm1'
    case 'CONFIRM':
      if (step === 'confirm1') return 'confirm2'
      return step
    case 'RESET':
      return 'form'
    default:
      return step
  }
}

/**
 * Where a write-back landed, from the server's answer: the themes variable
 * and its table, then sentiment and emotion when the run coded them (#1594).
 */
export function writtenFromResponse(data: WriteThemeAIVariableResponse): ThemeAIWrittenVariable {
  const companions: ThemeAIWrittenCompanion[] = []
  for (const kind of ['sentiment', 'emotion'] as const) {
    const c = data[kind]
    if (!c) continue
    companions.push({ kind, variable: c.variable, table: c.table, tableError: c.table_error, error: c.error })
  }
  return {
    variable: data.variable,
    folder: data.folder,
    customer: data.customer,
    job: data.job,
    table: data.table,
    tableError: data.table_error,
    ...(companions.length ? { companions } : {}),
  }
}
